/**
 * MetricCard - Premium metric card with count-up animation and glow effects
 *
 * Features:
 * - Animated number count-up on load/value change
 * - Subtle glow effect on hover
 * - Mini sparkline chart
 * - Smooth micro-interactions
 */

import React, { useEffect, useRef, useState } from "react";
import { motion, useSpring, useTransform, useInView } from "framer-motion";
import { Area, AreaChart, ResponsiveContainer, Tooltip } from "recharts";
import { branding } from "../config/branding";
import { useTheme } from "../context/ThemeContext";

type ColorTheme = "brand" | "green" | "blue" | "purple" | "red";

interface MetricCardProps {
  label: string;
  value: string | number;
  icon?: React.ReactNode;
  color: ColorTheme;
  data?: number[];
  tooltipFormatter?: (value: number) => string;
  index?: number; // For stagger animation
  percentChange?: number | null;
  sideLabel?: string;
  compact?: boolean;
  onClick?: () => void;
}

// Convert hex color to RGB components for use in rgba()
function hexToRgb(hex: string): string {
  const safeHex = hex ?? "";
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(safeHex);
  if (!result) return "99,102,241";
  return `${parseInt(result[1] ?? "0", 16)},${parseInt(result[2] ?? "0", 16)},${parseInt(result[3] ?? "0", 16)}`;
}

// Build color config dynamically from branding
function buildColorConfig(): Record<ColorTheme, {
  text: string;
  stroke: string;
  rgb: string;
}> {
  const brandRgb = hexToRgb(branding.colors.primary);
  const chartColors = branding.colors.chart;
  const greenHex = chartColors[5] || "#10B981";
  const blueHex = chartColors[6] || "#3B82F6";
  const purpleHex = chartColors[1] || "#8B5CF6";

  return {
    brand: {
      text: "text-[var(--brand-primary)]",
      stroke: branding.colors.primary,
      rgb: brandRgb,
    },
    green: {
      text: "text-emerald-500",
      stroke: "#10b981",
      rgb: "16,185,129",
    },
    blue: {
      text: "text-sky-400",
      stroke: blueHex,
      rgb: hexToRgb(blueHex || ""),
    },
    purple: {
      text: "text-purple-400",
      stroke: purpleHex,
      rgb: hexToRgb(purpleHex || ""),
    },
    red: {
      text: "text-red-500",
      stroke: "#ef4444",
      rgb: "239,68,68",
    },
  };
}

const colorConfig = buildColorConfig();

// Parse number from formatted string (handles $, K, M, %, commas)
function parseDisplayValue(value: string | number): number {
  if (typeof value === "number") return value;
  const cleaned = value.replace(/[$,%KM]/g, "").replace(/,/g, "");
  const num = parseFloat(cleaned);
  if (isNaN(num)) return NaN;
  let result = num;
  if (value.includes("M")) result *= 1000000;
  if (value.includes("K")) result *= 1000;
  return result;
}

// Format number back to display format
function formatToDisplay(num: number, originalValue: string): string {
  if (isNaN(num)) return originalValue;
  if (originalValue.includes("M")) {
    return `$${(num / 1000000).toFixed(2)}M`;
  }
  if (originalValue.includes("K")) {
    return `$${(num / 1000).toFixed(1)}K`;
  }
  if (originalValue.includes("%")) {
    return `${num.toFixed(1)}%`;
  }
  if (originalValue.startsWith("$")) {
    return `$${num.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
  }
  return num.toLocaleString("en-US", { maximumFractionDigits: 0 });
}

// Animated number component - only animates on initial load, not filter changes
function AnimatedNumber({ value, className }: { value: string; className?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const targetValue = parseDisplayValue(value);
  const hasAnimatedOnce = useRef(false);

  const spring = useSpring(0, {
    stiffness: 50,
    damping: 20,
    duration: 1.2,
  });

  const display = useTransform(spring, (current) => formatToDisplay(current, value));
  const [displayValue, setDisplayValue] = useState(formatToDisplay(0, value));

  // Only animate the first time the component becomes visible
  useEffect(() => {
    if (isInView && !hasAnimatedOnce.current && !isNaN(targetValue)) {
      hasAnimatedOnce.current = true;
      spring.set(targetValue);
    }
  }, [isInView, targetValue, spring]);

  useEffect(() => {
    const unsubscribe = display.on("change", (v) => setDisplayValue(v));
    return unsubscribe;
  }, [display]);

  // When value changes after initial animation, update instantly (no animation)
  useEffect(() => {
    if (hasAnimatedOnce.current && !isNaN(targetValue)) {
      spring.jump(targetValue);
    }
  }, [value, spring, targetValue]);

  return (
    <span ref={ref} className={className}>
      {displayValue}
    </span>
  );
}

// Default tooltip formatter
const defaultFormatter = (value: number) => value.toLocaleString();

export function MetricCard({
  label,
  value,
  icon,
  color,
  data = [],
  tooltipFormatter = defaultFormatter,
  index = 0,
  percentChange,
  sideLabel,
  compact = false,
  onClick,
}: MetricCardProps) {
  const { theme: appTheme } = useTheme();
  const isCrimson = appTheme === "crimson";
  const isDark = appTheme === "dark";
  const colorTheme = colorConfig[color];
  const chartData = data.map((v) => ({ value: v }));
  const gradientId = `${label.replace(/\s+/g, "-").toLowerCase()}-gradient`;
  const { rgb } = colorTheme;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.5,
        delay: index * 0.1,
        ease: [0.25, 0.46, 0.45, 0.94],
      }}
      whileHover={{
        y: -4,
        boxShadow: `0 0 50px -5px rgba(${rgb}, 0.5)`,
        transition: { duration: 0.2 }
      }}
      className={`relative overflow-hidden border rounded-2xl ${compact ? "p-3 md:p-4" : "p-6"} transition-shadow duration-500 ease-out backdrop-blur-sm ${onClick ? "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-ring)]" : ""}`}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={onClick ? (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick();
        }
      } : undefined}
      style={(isCrimson ? {
        background: "rgba(90, 6, 6, 0.55)",
        backdropFilter: "blur(12px)",
        borderColor: `rgba(${rgb}, 0.45)`,
        boxShadow: `0 0 30px -5px rgba(${rgb}, 0.35)`,
        ['--foreground']: '#ffffff',
        ['--muted-foreground']: 'rgba(255,255,255,0.7)',
      } : isDark ? {
        background: "rgba(14, 14, 14, 0.85)",
        borderColor: `rgba(${rgb}, 0.45)`,
        boxShadow: `0 0 45px -5px rgba(${rgb}, 0.55)`,
      } : {
        background: `linear-gradient(to bottom right, rgba(${rgb}, 0.2), rgba(${rgb}, 0.1), transparent)`,
        borderColor: `rgba(${rgb}, 0.2)`,
        boxShadow: `0 0 30px -5px rgba(${rgb}, 0.3)`,
      }) as React.CSSProperties}
    >
      {/* Subtle animated gradient overlay */}
      <motion.div
        className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-500"
        style={{
          background: `radial-gradient(circle at var(--mouse-x, 50%) var(--mouse-y, 50%), ${colorTheme.stroke}15, transparent 50%)`,
        }}
      />

      {/* Noise texture overlay */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        }}
      />

      <div className={`relative flex ${compact ? "flex-col items-start gap-2" : "items-center justify-between gap-3"}`}>
        <div className="space-y-3 flex-1 min-w-0">
          <motion.div
            className="flex items-center gap-2"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 + 0.2 }}
          >
            {icon && (
              <motion.div
                className={`size-5 ${colorTheme.text}`}
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                {icon}
              </motion.div>
            )}
            <span className="text-sm font-semibold text-foreground/80 uppercase tracking-wider">
              {label}
            </span>
          </motion.div>
          <div className="flex items-baseline gap-2">
            <AnimatedNumber
              value={String(value)}
              className="text-3xl font-bold text-foreground tracking-tight"
            />
            {percentChange != null && (
              <motion.span
                initial={{ opacity: 0, x: -5 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 + 0.4 }}
                className={`text-sm font-semibold ${
                  percentChange > 0
                    ? "text-emerald-400"
                    : percentChange < 0
                    ? "text-red-400"
                    : "text-muted-foreground"
                }`}
              >
                {percentChange > 0 ? "+" : ""}
                {percentChange.toFixed(2)}%
              </motion.span>
            )}
          </div>
        </div>
        {sideLabel && <p className={`text-xs leading-5 text-muted-foreground ${compact ? "w-full break-words border-t border-border/40 pt-2" : "max-w-[48%] text-right"}`}>{sideLabel}</p>}

        {/* Mini Chart — clipped so it never bleeds over the number */}
        {chartData.length > 0 && (
          <motion.div
            className="hidden xl:block w-28 h-14 shrink-0 overflow-hidden"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.1 + 0.3, duration: 0.4 }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={chartData}
                margin={{ top: 5, right: 2, left: 2, bottom: 5 }}
              >
                <defs>
                  <linearGradient
                    id={gradientId}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="0%" stopColor={colorTheme.stroke} stopOpacity={0.4} />
                    <stop offset="100%" stopColor={colorTheme.stroke} stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <Tooltip
                  cursor={{
                    stroke: colorTheme.stroke,
                    strokeWidth: 1,
                    strokeDasharray: "4 4",
                  }}
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const val = payload[0].value as number;
                      return (
                        <motion.div
                          initial={{ opacity: 0, y: 5 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="bg-background/95 backdrop-blur-md border border-border/50 shadow-xl rounded-lg p-2"
                        >
                          <p className="text-sm font-semibold text-foreground">
                            {tooltipFormatter(val)}
                          </p>
                        </motion.div>
                      );
                    }
                    return null;
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke={colorTheme.stroke}
                  fill={`url(#${gradientId})`}
                  strokeWidth={2}
                  dot={false}
                  animationDuration={1500}
                  animationEasing="ease-out"
                  activeDot={{
                    r: 5,
                    fill: colorTheme.stroke,
                    stroke: "white",
                    strokeWidth: 2,
                  }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </motion.div>
        )}
      </div>
      {!compact && sideLabel && (
        <span className="absolute right-5 bottom-4 text-[10px] font-medium leading-none text-muted-foreground whitespace-nowrap">
          {sideLabel}
        </span>
      )}
    </motion.div>
  );
}

// Skeleton version for loading state
export function MetricCardSkeleton({ index = 0 }: { index?: number }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: index * 0.05 }}
      className="relative overflow-hidden bg-card rounded-2xl border border-border p-6"
    >
      <div className="flex items-center justify-between">
        <div className="space-y-3 flex-1">
          <div className="flex items-center gap-2">
            <div className="size-5 rounded bg-muted animate-pulse" />
            <div className="h-4 w-24 rounded bg-muted animate-pulse" />
          </div>
          <div className="h-8 w-32 rounded bg-muted animate-pulse" />
        </div>
        <div className="w-32 h-16 rounded bg-muted/50 animate-pulse" />
      </div>
      {/* Shimmer overlay */}
      <motion.div
        className="absolute inset-0 -translate-x-full"
        animate={{ translateX: ["100%", "-100%"] }}
        transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
        style={{
          background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)",
        }}
      />
    </motion.div>
  );
}
